import * as React from "react";
import styled from "react-emotion";
import {Control, Field, Label, Select, TextArea} from "bloomer";
import Languages from "../../../../../components/model/Translation/Languages";
import getWordsCount from "../../../../../utils/WordsCount";
import InputWarning from "../../../../../elements/InputWarning";
import {getTranslationTokensCount} from "../../../../../api/models/Translation";
import WordsLabel from "../../../../../elements/WordsLabel";
import {INVALID_FIELD_STYLE} from "../../../../../utils/Constants";

const LanguagesWarning = styled("span")`
  text-align: center;
  vertical-align: top;
  font-size: 14px;
  line-height: 14px;
  color: red;
`;

const LanguageControl = styled(Control)`
  display: inline-block;
  width: 270px;
`;

const LanguageLabel = styled(Label)`
  display: inline-block;
  vertical-align: bottom;
  text-align: left;
  width: 60px;
`;

const TOKENS_LIMIT = 800;

interface ITranslationModelInputState {
    languages: any;
    loadingTokensCount: boolean;
    textInput: string;
    textInputInvalid: boolean;
    tokensCount: number;
    wordsCount: number;
    showLimitWarning: boolean;
    showSameLanguagesWarning: boolean;
    languageFrom: string;
    languageTo: string;
    languageFromInvalid: boolean;
    languageToInvalid: boolean
    errorMessage: string;
}

interface ITranslationModelInputProps {
    showUserInputsOnly?: boolean;
}

class TranslationModelInput extends React.Component<ITranslationModelInputProps, ITranslationModelInputState> {
    public constructor(props: ITranslationModelInputProps) {
        super(props);

        this.state = {
            languages: [],
            loadingTokensCount: false,
            textInput: "",
            textInputInvalid: false,
            tokensCount: 0,
            wordsCount: 0,
            showLimitWarning: false,
            showSameLanguagesWarning: false,
            languageFrom: "",
            languageTo: "",
            languageFromInvalid: false,
            languageToInvalid: false,
            errorMessage: ""
        };
    }

    public async componentDidMount () {
        const languages = await Languages.list();
        if (languages) {
            this.setState({
                languages
            });
        }
    }

    public getTokensCount = async () => {
        const text = this.state.textInput.trim();
        if (text) {
            this.setState({loadingTokensCount: true});
            const response = await getTranslationTokensCount(text);
            this.setState({loadingTokensCount: false});
            if (response) {
                return response.result;
            }
        }
        return 0;
    };

    public updateCounts = async () => {
        this.setState({
            wordsCount: getWordsCount(this.state.textInput.trim())
        })
        const tokensCount = await this.getTokensCount();
        this.setState({
            tokensCount,
            showLimitWarning: tokensCount > TOKENS_LIMIT
        });
    };

    public handleInputChange = (e: any) => {
        this.setState({
            textInput: e.target.value,
            textInputInvalid: false,
            errorMessage: ""
        }, () => {
            this.updateCounts();
        });
    };

    public validateForm = () => {
        if (this.props.showUserInputsOnly) {
            if (this.state.showSameLanguagesWarning || !this.state.languageFrom || !this.state.languageTo) {

                if (!this.state.languageFrom) {
                    this.setState({languageFromInvalid: true});
                }
                if (!this.state.languageTo) {
                    this.setState({languageToInvalid: true});
                }
                return false;
            }
            return true;
        } else {
            if (!this.state.textInput.trim() || this.state.loadingTokensCount
                || this.state.showLimitWarning || this.state.showSameLanguagesWarning
                || !this.state.languageFrom || !this.state.languageTo) {

                if (!this.state.textInput.trim()) {
                    this.setState({textInputInvalid: true});
                }
                if (!this.state.languageFrom) {
                    this.setState({languageFromInvalid: true});
                }
                if (!this.state.languageTo) {
                    this.setState({languageToInvalid: true});
                }
                return false;
            }
            return true;
        }
    };

    public getFormData = (modelNamePrefix: string) => {
        const data: any = {
            source_lang: this.state.languageFrom,
            destination_lang: this.state.languageTo
        };
        if (!this.props.showUserInputsOnly) {
            data.text = this.state.textInput.trim();
        }
        const formData = new FormData();
        Object.keys(data).forEach((key: string) => {
            formData.append(`${modelNamePrefix}.${key}`, data[key]);
        });
        return formData;
    };

    public resetForm = () => {
        this.setState({
            loadingTokensCount: false,
            textInput: "",
            textInputInvalid: false,
            tokensCount: 0,
            wordsCount: 0,
            showLimitWarning: false,
            showSameLanguagesWarning: false,
            languageFrom: "",
            languageTo: "",
            languageFromInvalid: false,
            languageToInvalid: false,
            errorMessage: ""
        });
    };

    public checkSelectedSameLanguages = () => {
        if (this.state.languageFrom && this.state.languageTo && this.state.languageFrom === this.state.languageTo) {
            this.setState({showSameLanguagesWarning: true});
        }
    };

    public handleSelectLanguageFromChange = (event: React.FormEvent<HTMLSelectElement>) => {
        this.setState({
            languageFrom: event.currentTarget.value,
            languageFromInvalid: false,
            showSameLanguagesWarning: false,
            errorMessage: ""
        }, this.checkSelectedSameLanguages);
    };

    public handleSelectLanguageToChange = (event: React.FormEvent<HTMLSelectElement>) => {
        this.setState({
            languageTo: event.currentTarget.value,
            languageToInvalid: false,
            showSameLanguagesWarning: false,
            errorMessage: ""
        }, this.checkSelectedSameLanguages);
    };

    public render() {
        return (
            <React.Fragment>
                <Field>
                    <LanguageLabel>From:</LanguageLabel>
                    <LanguageControl>
                        <Select
                            onChange={this.handleSelectLanguageFromChange}
                            value={this.state.languageFrom}
                        >
                            {
                                this.state.languages.map((lang: any) => {
                                    return <option key={lang.code} value={lang.code}>{lang.name}</option>;
                                })
                            }
                        </Select>
                    </LanguageControl>
                </Field>
                {
                    this.state.languageFromInvalid && <LanguagesWarning>Please select language.</LanguagesWarning>
                }
                {
                    this.state.showSameLanguagesWarning && <LanguagesWarning>The same languages are selected.</LanguagesWarning>
                }
                {
                    this.props.showUserInputsOnly ? <></> : <Field>
                        <Control>
                            <TextArea
                                style={this.state.showLimitWarning ? INVALID_FIELD_STYLE : undefined}
                                onChange={this.handleInputChange}
                                onPaste={this.handleInputChange}
                                value={this.state.textInput}
                                placeholder="Enter your text here"
                            />
                            <div>
                                {
                                    this.state.textInputInvalid && <InputWarning>Please input a text.</InputWarning>
                                }
                                {
                                    this.state.showLimitWarning &&
                                    <InputWarning>Input size limit has been exceeded. Please decrease the
                                        size.</InputWarning>
                                }
                                <WordsLabel>{this.state.wordsCount}&nbsp;{this.state.wordsCount > 1 ? 'words' : 'word'}</WordsLabel>
                            </div>
                        </Control>
                    </Field>
                }
                <Field>
                    <LanguageLabel>To:</LanguageLabel>
                    <LanguageControl>
                        <Select
                            onChange={this.handleSelectLanguageToChange}
                            value={this.state.languageTo}
                        >
                            {
                                this.state.languages.map((lang: any) => {
                                    return <option key={lang.code} value={lang.code}>{lang.name}</option>;
                                })
                            }
                        </Select>
                    </LanguageControl>
                </Field>
                {
                    this.state.languageToInvalid && <LanguagesWarning>Please select language.</LanguagesWarning>
                }
                {
                    this.state.showSameLanguagesWarning &&
                    <LanguagesWarning>The same languages are selected.</LanguagesWarning>
                }
            </React.Fragment>
        );
    }
}

export default TranslationModelInput;



// WEBPACK FOOTER //
// ./src/routes/Dashboard/CrossProvider/input/Translation/TranslationModelInput.tsx